import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Star, Calendar, Scissors, Award, Users } from 'lucide-react';
import { API_BASE_URL } from '../../config';
import { loadBarbers } from '../../utils/barbersCache';
import BarberServices from './BarberServices/BarberServices';
import ReservarCitaModal from './ReservarCitaModal/ReservarCitaModal';
import './Reservas.css';

export const mapBarberFromApi = (barber) => ({
    id: barber.id,
    nombre: barber.name || barber.nombre,
    especialidad: barber.specialty || barber.especialidad || 'Barbero Profesional',
    descripcion: barber.bio || barber.descripcion || 'Experto en cortes y estilos modernos',
    experiencia: barber.experience_years
        ? `${barber.experience_years} años`
        : barber.experiencia || 'Experiencia profesional',
    rating: barber.rating || 4.5,
    totalReseñas: barber.total_reviews || barber.totalReseñas || 0,
    disponible: barber.available !== false && barber.disponible !== false,
    servicios: barber.servicios || [],
    image_url: barber.image_url || null
});

const Reservas = ({ backLabel, onBack, clientData, onReservationSuccess }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const barberIdFromUrl = searchParams.get('barber');
    const barberFromState = location.state?.barber;
    const hasBarberFromState = !!(barberFromState?.id && barberFromState.id === barberIdFromUrl);

    const [step, setStep] = useState(() => (barberIdFromUrl ? 'service' : 'barber'));
    const [selectedService, setSelectedService] = useState(null);
    const [barberos, setBarberos] = useState([]);
    const [selectedBarbero, setSelectedBarbero] = useState(() =>
        hasBarberFromState ? mapBarberFromApi(barberFromState) : null
    );
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loadingBarberos, setLoadingBarberos] = useState(false);
    const [bootstrappingBarber, setBootstrappingBarber] = useState(
        () => !!barberIdFromUrl && !hasBarberFromState
    );
    const [error, setError] = useState(null);
    const barberResolvedRef = useRef(hasBarberFromState ? barberIdFromUrl : null);

    useEffect(() => {
        if (!barberIdFromUrl) {
            setBootstrappingBarber(false);
            barberResolvedRef.current = null;
            return;
        }

        if (barberResolvedRef.current === barberIdFromUrl) {
            setBootstrappingBarber(false);
            return;
        }

        let cancelled = false;

        const resolveBarberFromUrl = async () => {
            try {
                setBootstrappingBarber(true);
                setError(null);
                setStep('service');

                const barberosList = await loadBarbers(API_BASE_URL, mapBarberFromApi);
                const barber = barberosList.find((b) => b.id === barberIdFromUrl);

                if (cancelled) return;

                if (barber) {
                    setSelectedBarbero(barber);
                    barberResolvedRef.current = barberIdFromUrl;
                } else {
                    setError('Barbero no encontrado');
                    setStep('barber');
                    barberResolvedRef.current = null;
                    setSearchParams({});
                }
            } catch (err) {
                if (cancelled) return;
                console.error('Error resolving barber from URL:', err);
                setError(err.message);
                setStep('barber');
                barberResolvedRef.current = null;
                setSearchParams({});
            } finally {
                if (!cancelled) {
                    setBootstrappingBarber(false);
                }
            }
        };

        resolveBarberFromUrl();

        return () => {
            cancelled = true;
            setBootstrappingBarber(false);
        };
    }, [barberIdFromUrl, setSearchParams]);

    useEffect(() => {
        if (step !== 'barber' || barberIdFromUrl) return;

        const fetchBarberos = async () => {
            try {
                setLoadingBarberos(true);
                setError(null);

                const data = await loadBarbers(API_BASE_URL, mapBarberFromApi);
                setBarberos(data);
            } catch (err) {
                console.error('Error fetching barberos:', err);
                setError(err.message);
                setBarberos([]);
            } finally {
                setLoadingBarberos(false);
            }
        };

        fetchBarberos();
    }, [step, barberIdFromUrl]);

    const handleSelectBarber = (barbero) => {
        setSelectedBarbero(barbero);
        setSelectedService(null);
        setBootstrappingBarber(false);
        barberResolvedRef.current = barbero.id;
        setStep('service');
        setSearchParams({ barber: barbero.id });
    };

    const handleSelectService = (service) => {
        setSelectedService(service);
        setStep('appointment');
        setIsModalOpen(true);
    };

    const handleBackToBarbers = () => {
        setStep('barber');
        setSelectedBarbero(null);
        setSelectedService(null);
        setIsModalOpen(false);
        setBootstrappingBarber(false);
        barberResolvedRef.current = null;
        setSearchParams({});
    };

    const handleBackToServices = () => {
        setIsModalOpen(false);
        setSelectedService(null);
        setStep('service');
    };

    const handleCloseModal = () => {
        if (onBack) {
            setIsModalOpen(false);
            onBack();
        } else {
            handleBackToServices();
        }
    };

    const renderStars = (rating) => (
        <div className="rating-stars">
            {[...Array(5)].map((_, index) => (
                <Star
                    key={index}
                    size={16}
                    fill={index < Math.floor(rating) ? '#fbbf24' : 'none'}
                    stroke={index < Math.floor(rating) ? '#fbbf24' : '#d1d5db'}
                />
            ))}
        </div>
    );

    const getStepTitle = () => {
        if (step === 'service' && selectedBarbero) {
            return `Servicios de ${selectedBarbero.nombre}`;
        }
        if (step === 'service' && bootstrappingBarber) {
            return 'Preparando tu reserva';
        }
        switch (step) {
            case 'barber':
                return 'Reserva tu cita';
            case 'service':
                return 'Selecciona un servicio';
            case 'appointment':
                return 'Agenda tu cita';
            default:
                return 'Reserva tu cita';
        }
    };

    const getStepSubtitle = () => {
        if (step === 'service' && bootstrappingBarber) {
            return 'Cargando servicios del barbero seleccionado...';
        }
        switch (step) {
            case 'barber':
                return 'Elige tu barbero favorito para comenzar tu reserva';
            case 'service':
                return 'Selecciona el servicio que deseas reservar';
            case 'appointment':
                return 'Completa los datos para agendar tu cita';
            default:
                return '';
        }
    };

    return (
        <div className="reservas-page">
            <div className="reservas-header">
                {onBack ? (
                    <button type="button" className="back-button" onClick={onBack}>
                        <ArrowLeft size={20} />
                        <span>{backLabel || 'Volver'}</span>
                    </button>
                ) : (
                    <Link to="/" className="back-button">
                        <ArrowLeft size={20} />
                        <span>Volver al inicio</span>
                    </Link>
                )}
                <div className="reservas-header-content">
                    <motion.h1
                        key={`${step}-${selectedBarbero?.id || 'none'}`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        {getStepTitle()}
                    </motion.h1>
                    <motion.p
                        key={`${step}-subtitle-${selectedBarbero?.id || 'none'}`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                    >
                        {getStepSubtitle()}
                    </motion.p>
                </div>
            </div>

            {step === 'barber' && (
                <div className="barberos-container">
                    {loadingBarberos ? (
                        <div className="reservas-loading">
                            <div className="spinner"></div>
                            <p>Cargando barberos...</p>
                        </div>
                    ) : error ? (
                        <div className="reservas-error">
                            <p>Error: {error}</p>
                        </div>
                    ) : barberos.length === 0 ? (
                        <div className="reservas-empty">
                            <Users size={48} style={{ color: '#9ca3af', marginBottom: '1rem' }} />
                            <p>No hay barberos disponibles en este momento</p>
                        </div>
                    ) : (
                        <div className="barberos-grid">
                            {barberos.map((barbero, index) => (
                                <motion.div
                                    key={barbero.id}
                                    className="barbero-card"
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                    onClick={() => handleSelectBarber(barbero)}
                                >
                                    <div className="barbero-card-header">
                                        <div className="barbero-icon">
                                            {barbero.image_url ? (
                                                <img
                                                    src={
                                                        barbero.image_url.startsWith('http')
                                                            ? barbero.image_url
                                                            : `${API_BASE_URL}${barbero.image_url}`
                                                    }
                                                    alt={barbero.nombre}
                                                    className="barbero-icon-img"
                                                />
                                            ) : (
                                                <Scissors size={32} />
                                            )}
                                        </div>
                                        {barbero.disponible && (
                                            <span className="disponible-badge">Disponible</span>
                                        )}
                                    </div>

                                    <div className="barbero-info">
                                        <h3 className="barbero-nombre">{barbero.nombre}</h3>
                                        <p className="barbero-especialidad">{barbero.especialidad}</p>
                                        <p className="barbero-descripcion">{barbero.descripcion}</p>

                                        <div className="barbero-stats">
                                            <div className="stat-item">
                                                <Award size={18} />
                                                <span>{barbero.experiencia}</span>
                                            </div>
                                            <div className="stat-item">
                                                <div className="rating-container">
                                                    {renderStars(barbero.rating)}
                                                    <span className="rating-number">
                                                        {barbero.rating} ({barbero.totalReseñas} reseñas)
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <button type="button" className="reservar-btn">
                                        <Calendar size={20} />
                                        <span>Reservar con este barbero</span>
                                    </button>
                                </motion.div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {step === 'service' && bootstrappingBarber && (
                <div className="reservas-loading">
                    <div className="spinner"></div>
                    <p>Cargando servicios...</p>
                </div>
            )}

            {step === 'service' && selectedBarbero && !bootstrappingBarber && (
                <BarberServices
                    barber={selectedBarbero}
                    onSelectService={handleSelectService}
                    onBack={handleBackToBarbers}
                />
            )}

            {step === 'appointment' && isModalOpen && selectedBarbero && selectedService && (
                <ReservarCitaModal
                    barbero={selectedBarbero}
                    service={selectedService}
                    isOpen={isModalOpen}
                    onClose={handleCloseModal}
                    clientData={clientData}
                    onSuccess={onReservationSuccess}
                />
            )}
        </div>
    );
};

export default Reservas;
